import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { classNames } from "../../Utils";
import {
  acceptAllConsent,
  applyConsent,
  getConsent,
  rejectAllConsent,
  saveConsent,
} from "../../Utils/cookieConsent";

const COOKIE_POLICY_URL = "https://pajakind.com/cookie-policy";
const CHOICES_ID = "cookie-consent-choices";

const INLINE_ACTION =
  "rounded-sm text-blue-700 underline underline-offset-2 hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-1";

// Bottom bar asking for analytics and marketing consent, the web-app twin of
// the pajakind.com banner. A choice made on either site is shared through the
// .pajakind.com cookie, so a visitor who already answered on the landing site
// is not asked again here.
const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [customising, setCustomising] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    // Partner embeds run inside someone else's page, which owns consent there.
    if (window.location.pathname.startsWith("/embed")) return;
    applyConsent();
    setVisible(getConsent() === null);
  }, []);

  // The bar is fixed to the bottom of the window, so while it is open the end
  // of every page would sit underneath it. Reserve its height at the bottom of
  // the page (it grows when the choices open) and give the space back on close,
  // in the same paint as the bar appears and disappears. The space goes on
  // #root: html and body are pinned to the window height, so padding there
  // would land in the middle of a long page instead of after the footer.
  useLayoutEffect(() => {
    const banner = bannerRef.current;
    if (!visible || !banner) return undefined;
    const page = document.getElementById("root") || document.body;
    const previous = page.style.paddingBottom;
    const reserve = () => {
      page.style.paddingBottom = `${banner.offsetHeight}px`;
    };
    reserve();
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(reserve);
    observer?.observe(banner);
    return () => {
      observer?.disconnect();
      if (previous) page.style.paddingBottom = previous;
      else page.style.removeProperty("padding-bottom");
    };
  }, [visible]);

  if (!visible) return null;

  const close = (save) => {
    save();
    setVisible(false);
  };

  return (
    <section
      ref={bannerRef}
      aria-labelledby="cookie-consent-title"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white px-4 py-3 shadow-lg sm:px-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-6">
        <div className="text-sm leading-6 text-slate-700">
          <h2 id="cookie-consent-title" className="sr-only">
            {t("cookieConsent.title")}
          </h2>
          {/* One line on desktop, like pajakind.com: the reason, then the two
              secondary actions inline, so neither link wraps on its own. */}
          <p>
            {t("cookieConsent.description")}{" "}
            {!customising && (
              <>
                <button
                  type="button"
                  className={classNames("whitespace-nowrap", INLINE_ACTION)}
                  aria-expanded={customising}
                  aria-controls={CHOICES_ID}
                  onClick={() => setCustomising(true)}
                >
                  {t("cookieConsent.customise")}
                </button>
                <span aria-hidden="true" className="mx-1.5 text-slate-400">
                  ·
                </span>
              </>
            )}
            <a
              href={COOKIE_POLICY_URL}
              target="_blank"
              rel="noreferrer"
              className={classNames("whitespace-nowrap", INLINE_ACTION)}
            >
              {t("cookieConsent.policy")}
            </a>
          </p>
          {customising && (
            <fieldset id={CHOICES_ID} className="mt-2 space-y-1.5">
              <legend className="sr-only">{t("cookieConsent.choose")}</legend>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                  checked={analytics}
                  onChange={(event) => setAnalytics(event.target.checked)}
                />
                <span>{t("cookieConsent.analytics")}</span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                  checked={marketing}
                  onChange={(event) => setMarketing(event.target.checked)}
                />
                <span>{t("cookieConsent.marketing")}</span>
              </label>
            </fieldset>
          )}
        </div>
        {/* Refusing has to be as easy as accepting: both are full buttons of
            the same size, side by side, in every state of the banner. */}
        <div className="flex shrink-0 gap-2">
          <Button
            title="cookieConsent.reject"
            color="primary"
            outline
            className="flex-1 md:flex-none"
            data-testid="cookie-consent-reject"
            onClick={() => close(rejectAllConsent)}
          />
          {customising ? (
            <Button
              title="cookieConsent.save"
              color="primary"
              className="flex-1 md:flex-none"
              data-testid="cookie-consent-save"
              onClick={() =>
                close(() =>
                  saveConsent({ functional: false, analytics, marketing })
                )
              }
            />
          ) : (
            <Button
              title="cookieConsent.accept"
              color="primary"
              className="flex-1 md:flex-none"
              data-testid="cookie-consent-accept"
              onClick={() => close(acceptAllConsent)}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default CookieConsent;
